import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v6";
import { RouterProvider } from "react-router-dom";
import App from "./App";

import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { ImageCacheProvider } from "./global/ImageCacheContext";
import { AuthProvider } from "./components/user/BCPAuthProvider";
import { HelmetProvider } from "react-helmet-async";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import { ErrorBoundary } from "@datadog/browser-rum-react";
import { getEnvironmentVariable } from "./global/environmentVariables";
import { datadogLogs } from "@datadog/browser-logs";
import packageJson from "../package.json";
import { hasCookiePermission } from "./components/layout/cookies/CookieFunctions";
import { loadStylesheet } from "./styleSheets/StyleSheetFunctions";
import ErrorFallback from "./components/utilities/ErrorFallback";

const amplifyBranch = getEnvironmentVariable("AMPLIFY_BRANCH");

// Disables RUM and bugsnag in dev / local testing
const disableAnalytics =
  window?.location?.hostname === "localhost" ||
  getEnvironmentVariable("IS_DEV") === "true";

const DDAppId = getEnvironmentVariable("DATADOG_APP_ID");
const DDClientToken = getEnvironmentVariable("DATADOG_CLIENT_TOKEN");
const DDConsent = hasCookiePermission("dataDog");
const bugSnagConsent = hasCookiePermission("bugSnag");

if (DDConsent && !disableAnalytics) {
  datadogRum.init({
    applicationId: DDAppId,
    clientToken: DDClientToken,
    site: "datadoghq.com",
    service: amplifyBranch,
    env: amplifyBranch,
    version: packageJson.version,
    sessionSampleRate: 50,
    sessionReplaySampleRate: 0,
    defaultPrivacyLevel: "mask-user-input",
    plugins: [reactPlugin({ router: true })],
  });
}

datadogLogs.init({
  clientToken: DDClientToken,
  site: "datadoghq.com",
  service: amplifyBranch,
  forwardErrorsToLogs: true,
  sessionSampleRate: 100,
});

let BugsnagErrorBoundary = React.Fragment;

if (bugSnagConsent && !disableAnalytics) {
  Bugsnag.start({
    apiKey: "8437e6da3d015521ad932d28009d8a36",
    plugins: [new BugsnagPluginReact()],
  });

  BugsnagErrorBoundary = Bugsnag.getPlugin("react").createErrorBoundary(React);
}

const router = createBrowserRouter([
  {
    path: "/*",
    element: (
      <ErrorBoundary fallback={ErrorFallback}>
        <BugsnagErrorBoundary>
          <ImageCacheProvider>
            <AuthProvider>
              <HelmetProvider>
                <App />
              </HelmetProvider>
            </AuthProvider>
          </ImageCacheProvider>
        </BugsnagErrorBoundary>
      </ErrorBoundary>
    ),
  },
]);

function renderApp() {
  const root = ReactDOM.createRoot(document.getElementById("root"));

  root.render(
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>
  );
}

const STYLESHEET = getEnvironmentVariable("STYLESHEET");

loadStylesheet(STYLESHEET, renderApp);
